/**
 * The aquarium's pixel-art skins, drawn in code (M77): one small tile a
 * surface, the way Sable Reserve skins its ground (its M354), but made here
 * at run time from a fixed hash, so the game carries no image and owns every
 * pixel it draws. The same code gives the same tiles in every browser and in
 * Node (`scripts/tiles.ts` writes them out as PNGs for the test's record and
 * for reading by eye).
 *
 * Each tile is a handful of shades, darker ones cooler and lighter ones
 * warmer, laid from a noise that wraps at the tile's edges, so it repeats
 * with no seam, and ordered-dithered where one shade gives way to the next,
 * as pixel art shades a material; then what the surface is made of, a pixel
 * or a few at a time. The palettes and the patterns are the first cut's,
 * JUDGED to read as their surfaces; the look is the user's to choose
 * (`docs/AQUARIUM_ART.md`).
 *
 * No three.js and no DOM here: `skin.ts` lays the tiles on the aquarium.
 */

/** A tile's side, in pixels. */
export const SKIN_TILE_PX = 64;

/**
 * The skins drawn here, in the array texture's order: the reef's coral heads,
 * the islands' land (the user's own picture taken back out at their word:
 * "revert the land to what you drew before") and the rock the cutaway's
 * walls show, and the caustics' web (M78's; the user's own picture tried and
 * set aside at their word for this one). The seafloor's sand and the water's
 * ripple are the user's own pictures, at their own sizes (`scripts/tileArt.ts`).
 */
export const SKINS = ['reef', 'land', 'rock', 'caustic'] as const;
export type Skin = (typeof SKINS)[number];

/** The seed every tile is drawn from. Any fixed number: changing it redraws every tile. */
const SEED = 77;

/** Ordered dithering, 4 by 4, centered on nought: where a shade gives way to the next, the checker pixel art uses. */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16 - 0.5);

/** A hash of two integers and a seed, 0 to 1. */
function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(seed | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const wrap = (v: number, n: number): number => ((v % n) + n) % n;

/** Value noise over the tile, `cells` lattice squares across (a divisor of the tile's side), wrapped, smoothstepped. */
function valueNoise(cells: number, seed: number): Float64Array {
  const n = SKIN_TILE_PX;
  const out = new Float64Array(n * n);
  for (let y = 0; y < n; y++) {
    const ty = (y * cells) / n;
    const y0 = Math.floor(ty);
    const fy0 = ty - y0;
    const fy = fy0 * fy0 * (3 - 2 * fy0);
    for (let x = 0; x < n; x++) {
      const tx = (x * cells) / n;
      const x0 = Math.floor(tx);
      const fx0 = tx - x0;
      const fx = fx0 * fx0 * (3 - 2 * fx0);
      const at = (i: number, j: number): number => hash2(wrap(i, cells), wrap(j, cells), seed);
      const top = at(x0, y0) * (1 - fx) + at(x0 + 1, y0) * fx;
      const bottom = at(x0, y0 + 1) * (1 - fx) + at(x0 + 1, y0 + 1) * fx;
      out[y * n + x] = top * (1 - fy) + bottom * fy;
    }
  }
  return out;
}

/** Octaves of value noise, each [cells, weight], stretched to fill 0 to 1. */
function fbm(octaves: ReadonlyArray<readonly [number, number]>, seed: number): Float64Array {
  const total = new Float64Array(SKIN_TILE_PX * SKIN_TILE_PX);
  octaves.forEach(([cells, weight], k) => {
    const layer = valueNoise(cells, seed * 31 + k);
    for (let i = 0; i < total.length; i++) total[i] = (total[i] as number) + (layer[i] as number) * weight;
  });
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of total) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
  for (let i = 0; i < total.length; i++) total[i] = ((total[i] as number) - lo) / (hi - lo);
  return total;
}

/** A shade's index for a value 0 to 1 at a pixel, among `count`, dithered at each step. */
function shadeAt(value: number, x: number, y: number, count: number): number {
  const v = value * count + (BAYER[(y % 4) * 4 + (x % 4)] as number) * 0.9;
  return Math.max(0, Math.min(count - 1, Math.floor(v)));
}

/** A tile being drawn: its pixels as palette indices, set by `put`. */
class Canvas {
  readonly index = new Uint8Array(SKIN_TILE_PX * SKIN_TILE_PX);
  constructor(readonly palette: readonly number[]) {}
  put(x: number, y: number, shade: number): void {
    this.index[wrap(y, SKIN_TILE_PX) * SKIN_TILE_PX + wrap(x, SKIN_TILE_PX)] = shade;
  }
  get(x: number, y: number): number {
    return this.index[wrap(y, SKIN_TILE_PX) * SKIN_TILE_PX + wrap(x, SKIN_TILE_PX)] as number;
  }
  rgba(): Uint8Array {
    const out = new Uint8Array(this.index.length * 4);
    this.index.forEach((s, i) => {
      const c = this.palette[s] as number;
      out.set([(c >> 16) & 255, (c >> 8) & 255, c & 255, 255], i * 4);
    });
    return out;
  }
}

const N = SKIN_TILE_PX;

/**
 * Coral heads on rubble: one head a cell of 8 by 8, placed in it at random,
 * 2 to 4 pixels across, lit from the upper left; the rubble between them
 * dark, the crevices darker.
 */
function reef(): Canvas {
  const t = new Canvas([0x2e2420, 0x4a3b31, 0x6a5643, 0x8c7356, 0xad906a, 0xcdb082, 0xe8d2a6]);
  const rubble = fbm([[8, 1], [16, 0.5]], SEED + 3);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) t.put(x, y, shadeAt(rubble[y * N + x] as number, x, y, 3));
  const cell = 8;
  for (let cy = 0; cy < N / cell; cy++) {
    for (let cx = 0; cx < N / cell; cx++) {
      const r = 2 + hash2(cx, cy, SEED + 4) * 2.2;
      const ox = cx * cell + 1 + Math.floor(hash2(cx, cy, SEED + 5) * (cell - 2));
      const oy = cy * cell + 1 + Math.floor(hash2(cx, cy, SEED + 6) * (cell - 2));
      const reach = Math.ceil(r) + 1;
      for (let dy = -reach; dy <= reach; dy++) {
        for (let dx = -reach; dx <= reach; dx++) {
          const d = Math.hypot(dx, dy) / r;
          if (d > 1) {
            // A crevice ring around the head, on the side away from the light.
            if (d < 1.35 && dx + dy > 0) t.put(ox + dx, oy + dy, 0);
            continue;
          }
          // Brighter toward the upper left, as a dome lit from there.
          const lit = 0.55 + 0.45 * (-(dx + dy) / (2 * r)) + 0.25 * (1 - d);
          t.put(ox + dx, oy + dy, 2 + shadeAt(Math.max(0, Math.min(1, lit)), ox + dx, oy + dy, 5));
        }
      }
    }
  }
  return t;
}

/** Scrub and grass: a mottle, with tufts of two pixels, dark under light, and bare earth in patches. */
function land(): Canvas {
  const t = new Canvas([0x404a30, 0x56613f, 0x6d784f, 0x858f60, 0x9ea774, 0xb7b98a, 0x8a7458]);
  const field = fbm([[4, 1], [8, 0.7], [32, 0.25]], SEED + 7);
  const bare = fbm([[4, 1], [16, 0.4]], SEED + 8);
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const i = y * N + x;
      t.put(x, y, (bare[i] as number) > 0.82 && hash2(x, y, SEED + 9) < 0.7 ? 6 : shadeAt(field[i] as number, x, y, 5));
    }
  }
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      if (hash2(x, y, SEED + 10) < 0.06 && t.get(x, y) !== 6) {
        t.put(x, y, 0);
        t.put(x, y - 1, 4);
      }
    }
  }
  return t;
}

/**
 * Stacked lava flows, as the islands are built (each a band 3 to 9 pixels
 * thick, its top a rubbly reddish crust, its body cracked into columns,
 * its gas bubbles single dark pixels).
 */
function rock(): Canvas {
  const t = new Canvas([0x231f1d, 0x342d29, 0x463c35, 0x5a4c42, 0x6e5d50, 0x7a4b37, 0x94604a]);
  const field = fbm([[8, 1], [16, 0.5]], SEED + 11);
  const bands: number[] = [];
  for (let y = 0, k = 0; y < N; k++) {
    const thick = Math.min(N - y, 3 + Math.floor(hash2(k, 0, SEED + 12) * 7));
    for (let j = 0; j < thick; j++) bands.push(k);
    y += thick;
  }
  for (let y = 0; y < N; y++) {
    const band = bands[y] as number;
    const top = y === 0 || bands[y - 1] !== band;
    const lighter = band % 2 === 0 ? 1 : 0;
    for (let x = 0; x < N; x++) {
      const v = field[y * N + x] as number;
      if (top) t.put(x, y, 5 + shadeAt(v, x, y, 2));
      else t.put(x, y, 1 + lighter + shadeAt(v, x, y, 3));
    }
  }
  // Cooling joints: dark columns down each flow, a few a flow, wherever its hash puts them.
  for (let x = 0; x < N; x++) {
    for (let y = 1; y < N; y++) {
      const band = bands[y] as number;
      if (bands[y - 1] === band && hash2(x, band, SEED + 13) < 0.09) t.put(x, y, 0);
      else if (hash2(x, y, SEED + 14) < 0.02) t.put(x, y, 0);
    }
  }
  return t;
}

/**
 * Caustics: the web of bright lines a wavy surface focuses sunlight into on a
 * shallow floor, drawn as the edges between cells (one cell's center a block
 * of 8 by 8 pixels, wrapped), brightest where two cells meet, gray between.
 * Neutral gray, since it is laid on as light, by its brightness alone.
 */
function caustic(): Canvas {
  const t = new Canvas([0x4a4a4a, 0x6a6a6a, 0x8c8c8c, 0xb4b4b4, 0xdcdcdc, 0xffffff]);
  const cell = 8;
  const cells = N / cell;
  const centers: Array<[number, number]> = [];
  for (let cy = 0; cy < cells; cy++) {
    for (let cx = 0; cx < cells; cx++) {
      centers.push([cx * cell + 1 + hash2(cx, cy, SEED + 30) * (cell - 2), cy * cell + 1 + hash2(cx, cy, SEED + 31) * (cell - 2)]);
    }
  }
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      // The nearest two centers, the tile wrapped: near an edge their distances are alike.
      let d1 = Infinity;
      let d2 = Infinity;
      const cx = Math.floor(x / cell);
      const cy = Math.floor(y / cell);
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const [px, py] = centers[wrap(cy + dy, cells) * cells + wrap(cx + dx, cells)] as [number, number];
          const d = Math.hypot(x + 0.5 - (px + (cx + dx - wrap(cx + dx, cells)) * cell), y + 0.5 - (py + (cy + dy - wrap(cy + dy, cells)) * cell));
          if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) d2 = d;
        }
      }
      const edge = Math.max(0, 1 - (d2 - d1) / 2.2);
      t.put(x, y, shadeAt(edge ** 1.5, x, y, 6));
    }
  }
  return t;
}

const DRAW: Record<Skin, () => Canvas> = { reef, land, rock, caustic };

/** A skin's tile: RGBA, 8 bits a channel, rows from the top, `SKIN_TILE_PX` square. */
export function skinTile(skin: Skin): Uint8Array {
  return DRAW[skin]().rgba();
}

/** An sRGB channel, 0 to 255, in linear light. */
export function toLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/**
 * A tile's mean color in linear light, what the surface divides it by: so
 * laid on, the tile changes the color the data gives the surface by nothing
 * on average, and adds only its pattern (Sable Reserve's M354). The GPU
 * filters and blends in linear light, so the mean is taken there too.
 */
export function linearMean(rgba: ArrayLike<number>): [number, number, number] {
  let r = 0;
  let g = 0;
  let b = 0;
  const pixels = rgba.length / 4;
  for (let i = 0; i < pixels; i++) {
    r += toLinear(rgba[i * 4] as number);
    g += toLinear(rgba[i * 4 + 1] as number);
    b += toLinear(rgba[i * 4 + 2] as number);
  }
  return [r / pixels, g / pixels, b / pixels];
}
